import { Formik } from 'formik';
import { get, isInteger, pick } from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode, createContext, useEffect, useState } from 'react';
import { STATE_OPTIONS } from '../components/helpers';
import { Icd } from '../containers/my-central/patientService';
import { DateFromISOToBrazilianFormat } from '../helpers/Date';
import { Yup } from '../helpers/Yup';
import { validateCPF } from '../helpers/cpf';
import { validateBirthDate, checkContainWordsRepeatWithMinLength, checkAddress, validateEmail } from '../helpers/validations';
import { onlyDigits } from '../helpers/string';
import { getCountryByName } from '../shared/countries';
import { validateInternationalPhone } from '../shared/internationalPhoneRules';
import { usePrescriptionStore } from '../stores/prescriptionStore';

export interface EmissionContextProps extends EmissionProviderForm { }

const formDefaultValues = (): EmissionProviderForm => ({
  emissionEmail: '',
  emissionPhone: '',
  generatePdf: false,
  showDateInPdf: true,
  openPatientApp: true,

  icd: null,
  patientAddress: '',
  patientCity: '',
  patientFederalUnit: '',
  patientNumber: '',
  patientNeighborhood: '',
  patientPostalCode: '',
  patientBirthDate: '',
  patientGender: '',
  patientComplement: '',
  patientDocument: '',
  patientNeedToFillAddress: false,
  patientNeedToFillBirthdate: false,
  patientNeedToFillGender: false,
  patientNeedToFillIcd: false,
  patientNeedToFillDocument: false,
  patientNeedToFillNumber: false,

  establishmentFederalUnit: '',
  establishmentAddress: '',
  establishmentCity: '',
  establishmentComplement: '',
  establishmentNeighborhood: '',
  establishmentNumber: '',
  establishmentPostalCode: '',
  establishmentNeedToFill: false,
  establishmentHouseWithoutNumber: false,
  issueMedicinesInSeparateTabs: false,

  patientIsForeigner: false,
  patientWithoutCpf: false,
  patientPassport: '',
  patientCountry: '',
  hasPortaria344: false,
  enableMandatoryCpfPortaria344: false,
});

export const EmissionContext = createContext<EmissionContextProps>(formDefaultValues());

const requiredIfTrue = {
  is: true,
  // não é o ideal, massssss, não achei outra forma de ficar reaproveitável :/
  then: (schema: any) => schema.required(),
  otherwise: (schema: any) => schema,
};

const requiredValidateDataRepeat = {
  is: true,
  then: (schema: any) =>
    schema
      .test('requiredValidateDataRepeat', 'Texto com palavras repetidas e valores', (value: string) => {
        const valueForTest = value ?? '';
        return checkContainWordsRepeatWithMinLength(valueForTest, 2);
      })
      .required(),
  otherwise: (schema: any) => schema,
};

const validateNumberIfTrue = {
  is: true,
  then: (schema: any) =>
    schema
      .test('validateNumber', 'Informe um número válido', (value: string) => {
        if (!value) return false;
        const parsed = parseInt(value, 10);
        const valIsInt = isInteger(parsed);
        return value !== '' && valIsInt && parsed > 0;
      })
      .required(),
};

const EmissionFormSchema = Yup.object().shape({
  emissionPhone: Yup.string()
    .required('Informe um número de telefone válido.')
    .test('validate phoneNumber', 'Informe um número de celular válido.', async (value) => {
      if (!value) {
        return false;
      }

      return validateInternationalPhone(value);
    }),
  emissionEmail: Yup.string().test('emailValidation', 'Informe um e-mail válido', (val) => {
    if (!val) return true;
    return validateEmail(val);
  }),
  generatePdf: Yup.boolean(),
  showDateInPdf: Yup.boolean(),
  icd: Yup.object().when('patientNeedToFillIcd', {
    is: true,
    then: (schema) => schema.required('Informe um diagnóstico válido'),
    otherwise: (schema) => schema.nullable(),
  }),
  patientAddress: Yup.string().when('patientNeedToFillAddress', {
    is: true,
    then: (schema) =>
      schema.test('validatePatientAddress', 'Endereço inválido', (value) => {
        const address = value ?? '';
        return checkAddress(address);
      }),
    otherwise: (schema) => schema,
  }),
  patientCity: Yup.string().when('patientNeedToFillAddress', requiredValidateDataRepeat),
  patientFederalUnit: Yup.string().when('patientNeedToFillAddress', {
    is: true,
    then: (schema) =>
      schema
        .test('validateFederalUnitPatient', 'UF inválida', (value) => {
          const ufFortest = value ?? '';
          return STATE_OPTIONS.includes(ufFortest);
        })
        .required('UF inválida'),
  }),
  patientNumber: Yup.string().when('patientNeedToFillNumber', validateNumberIfTrue),
  patientNeighborhood: Yup.string().when('patientNeedToFillAddress', requiredValidateDataRepeat),
  patientPostalCode: Yup.string().when('patientNeedToFillAddress', requiredIfTrue),
  patientComplement: Yup.string(),
  patientBirthDate: Yup.string().when('patientNeedToFillBirthdate', {
    is: true,
    then: (schema) =>
      schema
        .test('birthDateValidation', 'Informe uma data de nascimento válida', (val) => {
          if (!val) return false;
          return validateBirthDate(val);
        })
        .required('Informe uma data de nascimento'),
  }),
  patientGender: Yup.string().when('patientNeedToFillGender', {
    is: true,
    then: (schema) => schema.oneOf(['Masculino', 'Feminino']),
    otherwise: (schema) => schema,
  }),
  patientDocument: Yup.string()
    .when(['hasPortaria344', 'enableMandatoryCpfPortaria344', 'patientIsForeigner', 'patientWithoutCpf'], {
      is: (hasPortaria344: boolean, enableMandatoryCpfPortaria344: boolean, patientIsForeigner: boolean, patientWithoutCpf: boolean) =>
        hasPortaria344 && enableMandatoryCpfPortaria344 && !patientIsForeigner && !patientWithoutCpf,
      then: (schema) =>
        schema
          .test('required', 'Informe um CPF válido', (value) => {
            return !!(value && value.trim() !== '');
          })
          .test('validateCpfRequired', 'Informe um cpf válido', (value) => {
            if (!value || value.trim() === '') return false;
            return validateCPF(value);
          }),
      otherwise: (schema) =>
        schema.test('validateCpf', 'Informe um cpf válido', (value) => {
          if (!value || value.trim() === '') return true;
          return validateCPF(value);
        }),
    }
    ),

  establishmentFederalUnit: Yup.string().when('establishmentNeedToFill', {
    is: true,
    then: (schema) =>
      schema
        .test('validateFederalUnitEstablishment', 'UF inválida', (value) => {
          const ufFortest = value ?? '';
          return STATE_OPTIONS.includes(ufFortest);
        })
        .required('UF inválida'),
  }),
  establishmentCity: Yup.string().when('establishmentNeedToFill', requiredValidateDataRepeat),
  establishmentAddress: Yup.string().when('establishmentNeedToFill', {
    is: true,
    then: (schema) =>
      schema.test('validatePatientAddress', 'Endereço inválido', (value) => {
        const address = value ?? '';
        return checkAddress(address);
      }),
    otherwise: (schema) => schema,
  }),
  establishmentComplement: Yup.string(),
  establishmentNeighborhood: Yup.string().when('establishmentNeedToFill', requiredValidateDataRepeat),
  establishmentNumber: Yup.string().when('establishmentNeedToFill', {
    is: true,
    then: (schema) =>
      schema.when('establishmentHouseWithoutNumber', {
        is: true,
        then: (schema) => schema,
        otherwise: (schema) => schema.required(),
      }),
    otherwise: (schema) => schema,
  }),
  establishmentPostalCode: Yup.string().when('establishmentNeedToFill', requiredIfTrue),
  patientPassport: Yup.string().when(
    ['patientIsForeigner', 'hasPortaria344', 'enableMandatoryCpfPortaria344', 'patientNeedToFillDocument'],
    {
      is: (
        patientIsForeigner: boolean,
        hasPortaria344: boolean,
        enableMandatoryCpfPortaria344: boolean,
        patientNeedToFillDocument: boolean
      ) => patientIsForeigner && hasPortaria344 && enableMandatoryCpfPortaria344 && patientNeedToFillDocument,
      then: (schema) =>
        schema.test('required', 'Informe um passaporte válido', (value) => {
          return !!(value && value.trim() !== '');
        }),
      otherwise: (schema) => schema,
    }
  ),
  patientCountry: Yup.string().when(
    ['patientIsForeigner', 'hasPortaria344', 'enableMandatoryCpfPortaria344', 'patientNeedToFillDocument'],
    {
      is: (
        patientIsForeigner: boolean,
        hasPortaria344: boolean,
        enableMandatoryCpfPortaria344: boolean,
        patientNeedToFillDocument: boolean
      ) => patientIsForeigner && hasPortaria344 && enableMandatoryCpfPortaria344 && patientNeedToFillDocument,
      then: (schema) =>
        schema.nullable().test('patientCountry', 'Informe um país de emissão válido', function (value) {
          if (!value || value === '' || value.trim() === '') {
            return false;
          }
          return value.length > 0;
        }),
      otherwise: (schema) => schema.nullable(),
    }
  ),
});

export function EmissionProvider(rootProps: { children: ReactNode }) {
  const { prescription, prescriberConfiguration } = usePrescriptionStore();

  const [formInitialValue, setFormInitialValue] = useState(formDefaultValues());

  useEffect(() => {
    const patient = prescription?.Paciente;
    const establishment = prescription?.Estabelecimento;

    if (!patient || !establishment) return;

    const establishmentFormFromPrescription = establishment.Endereco;

    const patientAddressFromPrescription = patient.Endereco || {};

    const patientFields = pick(patientAddressFromPrescription, [
      'Endereco1',
      'Endereco2',
      'Cidade',
      'Estado',
      'CodigoPostal',
      'Bairro',
      'Sexo',
      'Numero',
    ]);

    const establishmentFields = pick(establishmentFormFromPrescription, [
      'Endereco1',
      'Endereco2',
      'Cidade',
      'Estado',
      'CodigoPostal',
      'Bairro',
      'Numero',
    ]);

    const isDateValid = DateTime.fromISO(patient.Nascimento || '').isValid;

    const hasCpf = !!patient.Documento;
    const hasPassport = !!patient.Passaporte?.Numero;
    const foundCountry = patient.Passaporte?.PaisEmissor
      ? getCountryByName(patient.Passaporte.PaisEmissor.trim())
      : null;

    const hasPortaria344 = prescription?.Itens?.some((medicine) => medicine.idPortaria344 !== 0) || false;
    const enableMandatoryCpfPortaria344 = get(prescriberConfiguration, 'Features.enableMandatoryDocumentPortaria344', true);

    const needToFillDocument = !patient.Documento && !patient.Passaporte?.Numero;

    const payload = {
      establishmentAddress: establishmentFields.Endereco1,
      establishmentComplement: establishmentFields.Endereco2,
      establishmentCity: establishmentFields.Cidade,
      establishmentFederalUnit: establishmentFields.Estado,
      establishmentPostalCode: establishmentFields.CodigoPostal,
      establishmentNeighborhood: establishmentFields.Bairro,
      establishmentNumber: establishmentFields.Numero || '',
      patientAddress: patientFields.Endereco1 || '',
      patientComplement: patientFields.Endereco2 || '',
      patientCity: patientFields.Cidade || '',
      patientFederalUnit: patientFields.Estado || '',
      patientPostalCode: patientFields.CodigoPostal || '',
      patientNeighborhood: patientFields.Bairro || '',
      patientBirthDate: isDateValid ? DateFromISOToBrazilianFormat(patient.Nascimento || '') : '',
      patientGender: patient.Sexo,
      patientNumber: patientFields.Numero || '',
      emissionEmail: get(patient, 'Emails[0]', ''),
      emissionPhone: get(patient, 'Telefones[0]', ''),
      patientNeedToFillDocument: needToFillDocument,
      patientDocument: patient.Documento || '',
      patientIsForeigner: hasPassport && !hasCpf,
      patientPassport: patient.Passaporte?.Numero || '',
      patientCountry: foundCountry?.id || '',
      hasPortaria344,
      enableMandatoryCpfPortaria344,
    };

    setFormInitialValue((prev) => {
      return { ...prev, ...payload };
    });
  }, [prescription?.Paciente, prescription?.Estabelecimento, prescription?.Itens, prescriberConfiguration]);

  return (
    <Formik
      enableReinitialize
      validationSchema={EmissionFormSchema}
      initialValues={formInitialValue}
      onSubmit={() => { }}
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {(props) => <EmissionContext.Provider value={props.values}>{rootProps.children}</EmissionContext.Provider>}
    </Formik>
  );
}

export interface EmissionProviderForm {
  emissionPhone: string;
  emissionEmail: string;
  generatePdf: boolean;
  showDateInPdf: boolean;
  openPatientApp: boolean;

  icd: Icd | null;
  patientAddress: string;
  patientCity: string;
  patientFederalUnit: string;
  patientNumber: string;
  patientNeighborhood: string;
  patientPostalCode: string;
  patientBirthDate: string;
  patientGender: string;
  patientComplement: string;
  patientDocument: string;
  patientNeedToFillAddress: boolean;
  patientNeedToFillBirthdate: boolean;
  patientNeedToFillGender: boolean;
  patientNeedToFillIcd: boolean;
  patientNeedToFillDocument: boolean;
  patientNeedToFillNumber: boolean;

  establishmentFederalUnit: string;
  establishmentAddress: string;
  establishmentCity: string;
  establishmentNumber: string;
  establishmentNeighborhood: string;
  establishmentPostalCode: string;
  establishmentComplement: string;
  establishmentNeedToFill: boolean;
  establishmentHouseWithoutNumber: boolean;
  issueMedicinesInSeparateTabs: boolean;

  patientIsForeigner: boolean;
  patientWithoutCpf: boolean;
  patientPassport: string;
  patientCountry: string;
  hasPortaria344: boolean;
  enableMandatoryCpfPortaria344: boolean;
}

const validateCellphone = (cellphone: string): boolean => {
  const regex = /^\(?(?:[14689][1-9]|2[12478]|3[1234578]|5[1345]|7[134579])\)? ?(?:[2-8]|9[1-9])[0-9]{3}-?[0-9]{4}$/i;
  return regex.test(onlyDigits(cellphone));
};
